import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface NotificationData {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  autoClose?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<NotificationData[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private notifications: NotificationData[] = [];

  private unreadCountSubject = new BehaviorSubject<number>(10);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  /**
   * Mostra notifica di successo
   */
  showSuccess(message: string, title?: string, duration: number = 8000): void {
    this.addNotification({
      type: 'success',
      title: title || 'Successo',
      message,
      duration,
      autoClose: true
    });
  }

  /**
   * Mostra notifica di errore
   */
  showError(message: string, title?: string, autoClose: boolean = false): void {
    this.addNotification({
      type: 'error',
      title: title || 'Errore',
      message,
      autoClose
    });
  }

  /**
   * Mostra notifica di warning
   */
  showWarning(message: string, title?: string, duration: number = 6000): void {
    this.addNotification({
      type: 'warning',
      title: title || 'Attenzione',
      message,
      duration,
      autoClose: true
    });
  }

  /**
   * Mostra notifica informativa
   */
  showInfo(message: string, title?: string, duration: number = 5000): void {
    this.addNotification({
      type: 'info',
      title: title || 'Informazione',
      message,
      duration,
      autoClose: true
    });
  }

  /**
   * Aggiunge una notifica
   */
  addNotification(notification: Omit<NotificationData, 'id'>): void {
    const id = this.generateId();
    const newNotification: NotificationData = {
      id,
      autoClose: true,
      duration: 5000,
      ...notification
    };

    this.notifications.push(newNotification);
    this.notificationsSubject.next([...this.notifications]);

    if (newNotification.autoClose) {
      setTimeout(() => {
        this.removeNotification(id);
      }, newNotification.duration);
    }
  }

  /**
   * Rimuove una notifica
   */
  removeNotification(id: string): void {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.notificationsSubject.next([...this.notifications]);
  }

  /**
   * Pulisce tutte le notifiche
   */
  clearAll(): void {
    this.notifications = [];
    this.notificationsSubject.next([]);
  }

  /**
   * Ottiene tutte le notifiche
   */
  getNotifications(): NotificationData[] {
    return [...this.notifications];
  }

  /**
   * Genera ID univoco
   */
  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  /**
   * Marca tutte le notifiche come lette
   */
  markAllRead() {
    this.unreadCountSubject.next(0);
  }
}
